import { notifications } from '@mantine/notifications';
import { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { NavigationRoutes } from 'common/constants';
import colors from 'common/constants/colors.styles';
import {
  ApiError,
  CommonParamInput,
  ExtendedApiResult,
} from 'common/repositories/common.model';
import FetchWrapperComponent from 'components/elements/fetch-wrapper-component';
import { IColumn } from 'components/elements/table-component/table-component';
import { SeparatorVertical } from 'components/styles';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import FlexibleColumnsProvider from 'hooks/use-flexible-columns';
import { DrawerRefType } from 'mantine-components/widget/drawer';
import Tabs, { TabData } from 'mantine-components/widget/tabs';
import React from 'react';
import UniskaiFilterDrawer from 'templates/filter/uniskai-filter-drawer';
import UniskaiPagination from 'templates/table/uniskai-pagination';
import UniskaiPivotTableDrawer from 'templates/table/uniskai-pivot-table-drawer';
import UniskaiTable, { UniskaiTableProps } from 'templates/table/uniskai-table';

import UniskaiListHeader, {
  UniskaiListHeaderProps,
} from './uniskai-list-header';

export interface UniskaiListTemplateProps<T = any, QueryInput = object>
  extends Omit<
    UniskaiListHeaderProps<T>,
    'query' | 'onOpenFilter' | 'onOpenPivot'
  > {
  useQuery: (
    input?: { params: CommonParamInput & QueryInput },
    options?: UseQueryOptions<ExtendedApiResult<T[]>, ApiError>,
  ) => UseQueryResult<ExtendedApiResult<T[]>, ApiError>;
  queryInput?: { params: CommonParamInput & QueryInput };
  columns: IColumn<T>[];
  tableName: NavigationRoutes;

  tabs?: TabData[];
  tabValue?: string;

  onOpenDetail?: UniskaiTableProps<T>['onOpenDetail'];
  onRowClick?: UniskaiTableProps<T>['onRowClick'];
  rowLink?: UniskaiTableProps<T>['rowLink'];

  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
}

export default function UniskaiListTemplate<T, QueryInput = CommonParamInput>(
  props: UniskaiListTemplateProps<T, QueryInput>,
) {
  const {
    columns: _columns,
    tabs = [],
    page,
    setPage,
    // header
    title,
    addNavigation,
    showAdd,
    menu,
    menuProps,
    additionalRefresh,
    // table
    onRowClick,
    onOpenDetail,
    rowLink,
  } = props;
  const { useQuery, queryInput } = props;
  const {
    query,
    extras: [{ filters = [], setFilters }, { columns }],
  } = useComposedQuery(
    useQuery,
    queryInput,
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  const error = query.error;

  const items = React.useMemo(() => {
    return query?.data?.data || [];
  }, [query?.data?.data]);

  const meta = React.useMemo(() => {
    return query?.data?.meta;
  }, [query?.data?.meta]);

  const isLoading = React.useMemo(() => {
    return query?.isLoading;
  }, [query?.isLoading]);

  const filterRef = React.useRef<DrawerRefType>(null);
  const pivotRef = React.useRef<DrawerRefType>(null);

  const onOpenFilter = React.useCallback(() => {
    filterRef.current?.open();
  }, []);

  const onOpenPivot = React.useCallback(() => {
    pivotRef.current?.open();
  }, []);

  React.useEffect(() => {
    if (query.error === null) return;
    notifications.show({
      message: query.error.message,
      color: 'red',
      title: 'Error',
    });
  }, [query.error]);

  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
      }}
    >
      <FlexibleColumnsProvider columns={columns} tableName={props.tableName}>
        {(columns) => {
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                position: 'relative',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                  marginBottom: 16,
                }}
              >
                <UniskaiListHeader
                  onOpenFilter={onOpenFilter}
                  onOpenPivot={onOpenPivot}
                  title={title}
                  query={query}
                  addNavigation={addNavigation}
                  showAdd={showAdd}
                  menu={menu}
                  menuProps={menuProps}
                  additionalRefresh={additionalRefresh}
                />
                <SeparatorVertical $margin={8} />
                {tabs.length > 0 && <Tabs value={props.tabValue} data={tabs} />}
              </div>
              <FetchWrapperComponent
                error={error}
                isLoading={isLoading}
                component={
                  <UniskaiTable
                    columns={columns}
                    data={items}
                    isLoading={isLoading}
                    meta={meta}
                    onOpenDetail={onOpenDetail}
                    onRowClick={onRowClick}
                    rowLink={rowLink}
                  />
                }
              />
              <div
                style={{
                  padding: 8,
                  backgroundColor: colors.white,
                }}
              >
                {!!meta && (
                  <UniskaiPagination
                    page={page}
                    setPage={setPage}
                    meta={meta}
                  />
                )}
              </div>
              <UniskaiPivotTableDrawer ref={pivotRef} />
            </div>
          );
        }}
      </FlexibleColumnsProvider>
      <UniskaiFilterDrawer ref={filterRef} />
    </FilterHandleParentProvider>
  );
}
